import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { facilityApi, getFacilityBusinessHours } from '../../api';
import { Facility } from '../../api_types';
import { useQuery } from '@tanstack/react-query';
import { 
  CheckCircleIcon, 
  XCircleIcon,
  ArrowPathIcon,
  CreditCardIcon,
  ClockIcon,
  BuildingOfficeIcon,
  UserPlusIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

interface QuickstartGuideProps {
  facility: Facility;
  businessId: string;
  onComplete: () => void;
  showSubscriptionFeatures: boolean;
}

interface Task {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  isComplete: boolean;
  action?: () => void;
  dependencies?: string[];
  hasMembers?: boolean;
  hasStaff?: boolean;
}

const QuickstartGuide: React.FC<QuickstartGuideProps> = ({ 
  facility, 
  businessId,
  onComplete, 
  showSubscriptionFeatures 
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const navigate = useNavigate();

  const { data: facilityCompleteStatus, isLoading } = useQuery({
    queryKey: ['facilityCompleteStatus', facility.id, businessId],
    queryFn: async () => {
      const result = await facilityApi.getFacilityCompleteStatus(facility.id, businessId);
      return result;
    },
    enabled: !!facility.id && !!businessId,
  });

  const { data: businessHours } = useQuery({
    queryKey: ['businessHours', facility.id, businessId],
    queryFn: () => getFacilityBusinessHours(businessId, facility.id),
    enabled: !!facility.id && !!businessId,
  });

  // Memoize task computation
  const tasks = useMemo(() => {
    if (!facilityCompleteStatus) return [];

    const hasBusinessHours = businessHours ? 
      Object.values(businessHours).every(day => 
        !day.isClosed && day.openTime && day.closeTime
      ) : false;

    const baseTasks: Task[] = [
      {
        id: 'stripe',
        title: 'Set up Stripe Payments',
        description: 'Connect your Stripe account to accept payments',
        icon: CreditCardIcon,
        isComplete: facilityCompleteStatus.facilityStatus.has_stripe,
        action: () => navigate(`/business/dashboard/payments`)
      },
      {
        id: 'business-hours',
        title: 'Configure Business Hours',
        description: 'Set operating hours for all days of the week',
        icon: ClockIcon,
        isComplete: hasBusinessHours,
        action: () => navigate(`/business/dashboard/courts`)
      },
      {
        id: 'courts',
        title: 'Add Courts',
        description: 'Set up your facility\'s courts',
        icon: BuildingOfficeIcon,
        isComplete: facilityCompleteStatus.courtCount > 0,
        action: () => navigate(`/business/dashboard/courts`)
      },
      {
        id: 'public-plans',
        title: 'Configure Public Access',
        description: 'Set up public use pricing for non-member court bookings',
        icon: GlobeAltIcon,
        isComplete: facilityCompleteStatus.pricingSettings.weekendRate > 0 && 
                   facilityCompleteStatus.pricingSettings.weekdayPeakRate > 0 && 
                   facilityCompleteStatus.pricingSettings.weekdayOffPeakRate > 0 &&
                   (facilityCompleteStatus.pricingSettings.peakHours.startTime !== "") &&
                   (facilityCompleteStatus.pricingSettings.peakHours.endTime !== "") &&
                   facilityCompleteStatus.publicAccessSettings.openToPublic,
        action: () => navigate(`/business/dashboard/public-use`),
        dependencies: ['stripe', 'courts']
      },
      ...(facility.isPPU ? [] : [{
        id: 'member-invites',
        title: 'Invite Your First Member',
        description: 'Generate invite codes for new members',
        icon: UserPlusIcon,
        isComplete: facilityCompleteStatus.facilityStatus.has_members,
        action: () => navigate(`/business/dashboard/invites`),
        dependencies: ['stripe', 'courts'],
        hasMembers: facilityCompleteStatus.facilityStatus.has_members
      }]),
      {
        id: 'staff-invites',
        title: 'Add Staff or Coaches',
        description: 'Invite staff members to help manage your facility',
        icon: BriefcaseIcon,
        isComplete: facilityCompleteStatus.facilityStatus.has_staff,
        action: () => navigate(`/business/dashboard/staff-management`),
        dependencies: ['stripe', 'courts'],
        hasStaff: facilityCompleteStatus.facilityStatus.has_staff
      }
    ];

    const subscriptionTasks: Task[] = showSubscriptionFeatures ? [
      {
        id: 'subscription-plans',
        title: 'Create Subscription Plans',
        description: 'Set up membership plans for your facility',
        icon: CurrencyDollarIcon,
        isComplete: facilityCompleteStatus.facilityStatus.has_plans,
        action: () => navigate(`/business/dashboard/payments`),
        dependencies: ['stripe']
      }
    ] : [];

    return [...baseTasks, ...subscriptionTasks];
  }, [facility, facilityCompleteStatus, showSubscriptionFeatures, navigate, businessId]);

  // Memoize task completion check
  const allTasksComplete = useMemo(() => {
    if (isLoading || !facilityCompleteStatus) return false;
    return tasks.length > 0 && tasks.every(task => task.isComplete);
  }, [tasks, isLoading, facilityCompleteStatus]);

  useEffect(() => {
    if (allTasksComplete) {
      onComplete();
    }
  }, [allTasksComplete, onComplete]);

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const isTaskEnabled = (task: Task) => {
    if (!task.dependencies) return true;
    return task.dependencies.every(depId => 
      tasks.find(t => t.id === depId)?.isComplete
    );
  };

  return (
    <div className={`bg-white shadow-xl rounded-2xl p-8 mb-6 ${allTasksComplete ? 'border border-green-200' : ''}`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <RocketLaunchIcon className="h-7 w-7 text-indigo-500" /> Getting Started
          </h2>
          {allTasksComplete && !isLoading && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              All tasks complete
            </span>
          )}
        </div>
        <button
          onClick={handleCollapse}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200"
        >
          {isCollapsed ? 'Show' : 'Hide'}
        </button>
      </div>
      
      <div className={`transition-all duration-500 ease-in-out ${isCollapsed ? 'max-h-0 overflow-hidden' : 'max-h-[2000px]'}`}>
        {isLoading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading tasks...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tasks.map((task) => {
              const enabled = isTaskEnabled(task);
              return (
                <div
                  key={task.id}
                  className={`flex items-start p-4 rounded-lg ${
                    task.isComplete ? 'bg-green-50' : enabled ? 'bg-gray-50' : 'bg-gray-100'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {task.isComplete ? (
                      <CheckCircleIcon className="h-6 w-6 text-green-500" />
                    ) : (
                      <XCircleIcon className={`h-6 w-6 ${enabled ? 'text-gray-400' : 'text-gray-300'}`} />
                    )}
                  </div>
                  <div className="ml-3 flex-grow">
                    <div className="flex items-center justify-between">
                      <h3 className={`text-sm font-medium ${enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                        {task.title}
                      </h3>
                      {(task.hasMembers || task.hasStaff) && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {task.hasMembers ? 'Has Members' : 'Has Staff'}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${enabled ? 'text-gray-500' : 'text-gray-400'}`}>
                      {task.description}
                    </p>
                    {!task.isComplete && task.action && (
                      <button
                        onClick={task.action}
                        disabled={!enabled}
                        className={`mt-2 inline-flex items-center text-sm ${
                          enabled 
                            ? 'text-indigo-600 hover:text-indigo-500' 
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                        {enabled ? 'Complete this task' : 'Complete required tasks first'}
                      </button>
                    )}
                    {!enabled && task.dependencies && (
                      <p className="mt-1 text-xs text-gray-400">
                        Requires: {task.dependencies.map(dep => 
                          tasks.find(t => t.id === dep)?.title
                        ).join(' and ')}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickstartGuide; 